import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Camera } from "lucide-react";
import { SectionHead, FadeIn, EASE } from "./Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_REELS = [
  { id: "1", video_id: "OAud6fWM7sA", title: "Cinematic Banquet Showcase May 14th", category: "Events", duration: "0:25" },
  { id: "2", video_id: "9ILnjXIHCZo", title: "Kevin Joseph The Agency Introduction Video", category: "Commercials", duration: "0:40" },
  { id: "3", video_id: "89lwIrckNNc", title: "Professional Banquet Cinematic Video Shoot July 10th 2026", category: "Events", duration: "0:25" },
];

const PHOTOS = [
  { url: "/media/veloce-06.jpg", cat: "Automotive", exif: "50MM — F/1.8 — 1/1000 — ISO 500", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
  { url: "/media/veloce-44.jpg", cat: "Automotive", exif: "50MM — F/1.8 — 1/100 — ISO 320", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
  { url: "/media/m3-05.jpg", cat: "Automotive", exif: "50MM — F/1.8 — 1/125 — ISO 1250", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
  { url: "/media/m3-09.jpg", cat: "Automotive", exif: "50MM — F/1.8 — 1/250 — ISO 1000", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
  { url: "/media/m3-16.jpg", cat: "Automotive", exif: "50MM — F/1.8 — 1/250 — ISO 1000", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
  { url: "/media/ferrari-09.jpg", cat: "Automotive", exif: "50MM — F/1.8 — 1/200 — ISO 800", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
  { url: "/media/is350-6.jpg", cat: "Automotive", exif: "50MM — F/1.8 — 1/3200 — ISO 320", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
  { url: "/media/rs6-1.jpg", cat: "Automotive", exif: "50MM — F/2.0 — 1/500 — ISO 1600", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
  { url: "/media/brz-4.jpg", cat: "Automotive", exif: "50MM — F/1.8 — 1/1600 — ISO 320", cam: "Sony A7 III · FE 50mm F1.8", aspect: "aspect-[2/3]" },
];

const TABS = ["All", "Automotive", "Events", "Commercials"];

export default function Work() {
  const [tab, setTab] = useState("All");
  const [reels, setReels] = useState(FALLBACK_REELS);
  const [playingId, setPlayingId] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch(`${API}/reels`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => { if (Array.isArray(data) && data.length) setReels(data); })
      .catch(() => {});
  }, []);

  const videos = tab === "All" ? reels : reels.filter((r) => r.category.toLowerCase() === tab.toLowerCase());
  const photos = tab === "All" ? PHOTOS : PHOTOS.filter((p) => p.cat === tab);
  const nothing = videos.length === 0 && photos.length === 0;

  return (
    <section id="work" className="relative py-16 md:py-24" data-testid="work-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <SectionHead index="02" kicker="Selected Work" title="Frames &" accent="Reels" />

        <FadeIn className="mb-10 md:mb-14">
          <div className="flex flex-wrap justify-center gap-2">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`work-filter-${t.toLowerCase()}`}
                onClick={() => setTab(t)}
                className={`font-mono text-[11px] uppercase tracking-[0.22em] px-4 py-2 border transition-all duration-200 ${
                  tab === t
                    ? "border-gold text-gold bg-gold/10"
                    : "border-white/15 text-zinc-400 hover:border-gold/50 hover:text-gold"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </FadeIn>

        {nothing ? (
          <FadeIn>
            <div className="border border-dashed border-white/15 bg-surface/40 py-16 px-6 text-center" data-testid="work-empty-state">
              <Camera className="mx-auto text-gold/70 mb-4" size={28} />
              <p className="font-display uppercase tracking-tight text-xl text-zinc-200">Nothing in {tab} yet</p>
              <p className="text-sm text-zinc-500 mt-2">Fresh work is always in the edit bay — check back soon.</p>
            </div>
          </FadeIn>
        ) : (
          <>
            {videos.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-12 md:mb-20">
                {videos.map((v, i) => (
                  <FadeIn key={v.video_id} delay={i * 0.06} className={i === 0 ? "lg:col-span-12" : "lg:col-span-6"}>
                    <VideoCard reel={v} playing={playingId === v.video_id} onPlay={() => setPlayingId(v.video_id)} featured={i === 0} />
                  </FadeIn>
                ))}
              </div>
            ) : (
              <FadeIn>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-8" data-testid="reels-empty-note">
                  New {tab} reels dropping soon — stills below.
                </p>
              </FadeIn>
            )}

            {photos.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                {photos.map((p, i) => (
                  <FadeIn key={i} delay={(i % 3) * 0.05}>
                    <button
                      data-testid={`photo-card-${i}`}
                      onClick={() => setLightbox(p)}
                      className={`group relative block w-full ${p.aspect} overflow-hidden`}
                    >
                      <img
                        src={p.url}
                        alt={p.exif || "Portfolio photograph"}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
                      {p.exif && (
                        <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-left translate-y-2 group-hover:translate-y-0 opacity-90 group-hover:opacity-100 transition-all duration-300">
                          <p className="font-mono text-[10px] tracking-[0.2em] text-gold">{p.exif}</p>
                        </div>
                      )}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ring-1 ring-inset ring-gold/50" />
                    </button>
                  </FadeIn>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[200] bg-obsidian/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(null)}
            data-testid="photo-lightbox"
          >
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.url} alt="Gallery photograph" className="w-full max-h-[80vh] object-contain" />
              <figcaption className="mt-4 font-mono text-[11px] tracking-[0.2em] text-zinc-400 flex flex-col gap-1">
                {lightbox.cam && <span className="text-gold">{lightbox.cam}</span>}
                {lightbox.exif && <span>{lightbox.exif}</span>}
              </figcaption>
            </motion.figure>
            <button
              data-testid="lightbox-close-button"
              className="absolute top-5 right-5 text-zinc-300 hover:text-gold transition-colors p-2"
              onClick={() => setLightbox(null)}
              aria-label="Close"
            >
              <X size={28} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function VideoCard({ reel, playing, onPlay, featured }) {
  return (
    <div className={`relative group ${featured ? "aspect-[16/9] lg:aspect-[2.39/1]" : "aspect-video"}`} data-testid={`video-card-${reel.video_id}`}>
      <div className="absolute inset-0 overflow-hidden cinema-mask bg-surface">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${reel.video_id}?autoplay=1&rel=0&modestbranding=1`}
            title={reel.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid={`video-iframe-${reel.video_id}`}
          />
        ) : (
          <button className="absolute inset-0 w-full h-full text-left" onClick={onPlay} data-testid={`video-play-button-${reel.video_id}`} aria-label={`Play ${reel.title}`}>
            <img
              src={`https://i.ytimg.com/vi/${reel.video_id}/maxresdefault.jpg`}
              onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${reel.video_id}/hqdefault.jpg`; }}
              alt={reel.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/35 group-hover:bg-black/20 transition-colors duration-300" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 md:w-20 md:h-20 bg-gold text-black flex items-center justify-center group-hover:scale-110 group-hover:shadow-[0_0_36px_rgba(229,169,60,0.5)] transition-all duration-300">
                <Play size={26} fill="currentColor" className="ml-1" />
              </span>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}
